import { TooltipProvider } from "@/components/ui/tooltip";
import { AIChatRuntimeProvider } from "@/domains/ai/ai-chat";
import { useAppointmentSubscription } from "@/domains/appointment/appointments/shared/hooks/core/useAppointmentSubscription";
import { SalonSelectorPage } from "@/domains/onboarding/salon-selector/salon-selector-page";
import { useFranchiseLoader } from "@/hooks/use-franchise-loader";
import { useLanguagePreference } from "@/hooks/use-language-preference";
import { useInitializeSalonConfigurations } from "@/hooks/use-salon-configuration-integration";
import { useAppLifecycleRefetch } from "@/lib/apollo/use-app-lifecycle-refetch";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { NotificationLifecycleProvider } from "@/lib/push-notifications/component/simple-notification-provider";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { WhatsAppPromoProvider } from "@/lib/whatsapp-promo/whatsapp-promo-provider";
import { AppLayout } from "./app-layout";
import { GlobalDialogProvider } from "./global-dialog-provider";
import { useSalonAccessState } from "./hooks/use-salon-access-state";
import { SalonSuspendedScreen } from "./salon-suspended-screen";

export function AppContainer() {
  // Auth state — initialization is handled by AuthGuard before this component mounts
  const user = useAuthStore((state) => state.user);

  // Salon state
  const { currentSalon, isLoading: salonLoading } = useSalonStore();

  // Initialize franchise access data
  const { isLoading: franchiseLoading } = useFranchiseLoader();

  // Handle theme management
  // useThemeManager(currentView);

  // Initialize user language preference from backend
  const { isLoading: languageLoading } = useLanguagePreference();

  // Enable real-time appointment updates for the selected salon
  useAppointmentSubscription(currentSalon?.id ?? "", currentSalon?.timezone ?? "");

  // Refetch appointment queries when the app comes to the foreground or network reconnects.
  // This covers the case where the WebSocket subscription died silently during backgrounding.
  useAppLifecycleRefetch();

  // Initialize salon configuration management with real-time updates
  const { isLoading: configLoading } = useInitializeSalonConfigurations();

  // If no user is authenticated, don't render anything (handled by AuthGuard)
  if (!user) {
    return null;
  }

  // Show loading state while initial data is being loaded
  if (franchiseLoading || salonLoading || languageLoading || configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="text-gray-600">
            {franchiseLoading
              ? "Loading salon access..."
              : languageLoading
                ? "Loading language preferences..."
                : configLoading
                  ? "Loading salon configurations..."
                  : "Loading salons..."}
          </p>
        </div>
      </div>
    );
  }

  // If no salon is selected, show salon selector
  if (!currentSalon) {
    return <SalonSelectorPage />;
  }

  return <AppContainerWithAccessCheck currentSalonId={currentSalon.id} />;
}

interface AppContainerWithAccessCheckProps {
  currentSalonId: string;
}

function AppContainerWithAccessCheck({ currentSalonId }: AppContainerWithAccessCheckProps) {
  const isMobile = useIsMobile();
  const {
    suspended,
    reason,
    contactWhatsApp,
    loading: accessLoading,
  } = useSalonAccessState(currentSalonId);

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (suspended) {
    return <SalonSuspendedScreen reason={reason} contactWhatsApp={contactWhatsApp} />;
  }

  return (
    <NotificationLifecycleProvider>
      <TooltipProvider>
        <AIChatRuntimeProvider>
          <AppLayout isMobile={isMobile} />
          <GlobalDialogProvider />
          <WhatsAppPromoProvider />
        </AIChatRuntimeProvider>
      </TooltipProvider>
    </NotificationLifecycleProvider>
  );
}
